import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, User, Menu, X, LogOut, ChevronDown, Tag, Sparkles, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { motion, AnimatePresence } from 'framer-motion';
import USInsureVerifyModal from './USInsureVerifyModal';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUSInsureModal, setShowUSInsureModal] = useState(false);
  const { user, logout } = useAuth();
  const { cartItemsCount } = useCart();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${searchQuery}`);
      setSearchQuery('');
    }
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
      {/* Top Banner - Compact */}
      <div className="bg-[#1A1A1A] text-white text-center py-1.5 px-4 text-xs tracking-widest uppercase" data-testid="promo-banner">
        <span className="hidden sm:inline">Free Local Pickup • </span>
        Free Shipping Over $75 
        <span className="hidden sm:inline"> • Open Daily 9AM–9PM</span>
      </div>

      {/* US Insurance Promo Banner */}
      <div 
        className="bg-[#1A1A1A] text-white py-2 px-4" 
        data-testid="usinsure-banner"
      >
        <div className="flex items-center justify-center gap-3 sm:gap-4">
          <a 
            href="https://www.usiagencyllc.com/contact" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-2 hover:opacity-90 transition-opacity group"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_nepora-shop/artifacts/a78ikder_US%20Insurance%20agency%20logo.webp" 
              alt="US Insurance Agency LLC" 
              className="h-10 sm:h-12 w-auto"
            />
            <span className="text-[10px] sm:text-xs text-[#D4AF37] group-hover:text-white transition-colors font-medium underline underline-offset-2">
              Get a Free Quote
            </span>
          </a>
          <span className="text-gray-500 hidden sm:inline">|</span>
          <div 
            className="text-xs sm:text-sm font-light tracking-wide flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setShowUSInsureModal(true)}
          >
            <span className="text-[#D4AF37]">US Insurance Clients</span>
            <span className="text-gray-400 hidden sm:inline">—</span>
            <span>Enjoy your exclusive <span className="text-[#D4AF37] font-medium">10% OFF</span></span>
          </div>
        </div>
      </div>

      {/* US Insurance Verification Modal */}
      <USInsureVerifyModal 
        isOpen={showUSInsureModal} 
        onClose={() => setShowUSInsureModal(false)} 
      />

      {/* Main Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="flex items-center justify-between h-16 lg:h-[68px]">
            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 -ml-2 hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo">
              <span 
                className="text-2xl lg:text-[26px] font-bold tracking-wider text-[#1A1A1A]"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                NEPORA
              </span>
            </Link>

            {/* Desktop Navigation - Equal Spacing */}
            <nav className="hidden lg:flex items-center justify-center flex-1 mx-2 xl:mx-6">
              <div className="flex items-center space-x-3 lg:space-x-4 xl:space-x-6">
              {/* Clothing with Dropdown */}
              <div className="relative group">
                <span className="text-[10px] xl:text-xs uppercase tracking-widest font-medium hover:text-[#D4AF37] transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap" data-testid="nav-clothing">
                  Clothing
                  <ChevronDown className="w-3 h-3 transition-transform group-hover:rotate-180" />
                </span>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-white shadow-xl border border-gray-100 min-w-[160px]">
                    <div className="py-2">
                      <Link to="/category/clothing" className="block px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        All Clothing
                      </Link>
                      <Link to="/category/clothing?gender=women" className="block px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Women
                      </Link>
                      <Link to="/category/clothing?gender=men" className="block px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Men
                      </Link>
                      <Link to="/category/clothing?gender=kids" className="block px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Kids
                      </Link>
                      <Link to="/category/clothing?type=casual" className="block px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Casual
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Traditional with Dropdown */}
              <div className="relative group">
                <span className="text-[10px] xl:text-xs uppercase tracking-widest font-medium hover:text-[#D4AF37] transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap" data-testid="nav-traditional">
                  Traditional
                  <ChevronDown className="w-3 h-3 transition-transform group-hover:rotate-180" />
                </span>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-white shadow-xl border border-gray-100 min-w-[180px]">
                    <div className="py-2">
                      <Link to="/category/traditional-wear" className="block px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        All Traditional
                      </Link>
                      <div className="border-t border-gray-100 my-1 mx-4"></div>
                      <p className="px-5 py-1 text-[10px] text-gray-400 uppercase tracking-widest">Women</p>
                      <Link to="/category/traditional-wear?type=saree" className="block px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Saree
                      </Link>
                      <Link to="/category/traditional-wear?type=lehenga" className="block px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Lehenga
                      </Link>
                      <div className="border-t border-gray-100 my-1 mx-4"></div>
                      <p className="px-5 py-1 text-[10px] text-gray-400 uppercase tracking-widest">Men</p>
                      <Link to="/category/traditional-wear?type=kurtha&gender=men" className="block px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Kurtha
                      </Link>
                      <div className="border-t border-gray-100 my-1 mx-4"></div>
                      <p className="px-5 py-1 text-[10px] text-gray-400 uppercase tracking-widest">Kids</p>
                      <Link to="/category/traditional-wear?type=kurtha&gender=kids" className="block px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Kurtha
                      </Link>
                      <div className="border-t border-gray-100 my-1 mx-4"></div>
                      <p className="px-5 py-1 text-[10px] text-gray-400 uppercase tracking-widest">Cultural</p>
                      <Link to="/category/traditional-wear?type=sherpa" className="block px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Sherpa
                      </Link>
                      <Link to="/category/traditional-wear?type=tamang" className="block px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Tamang
                      </Link>
                      <Link to="/category/traditional-wear?type=cultural" className="block px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Other Groups
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Beauty */}
              <Link to="/category/beauty" className="text-[10px] xl:text-xs uppercase tracking-widest font-medium hover:text-[#D4AF37] transition-colors whitespace-nowrap" data-testid="nav-beauty">
                Beauty
              </Link>
              
              {/* Jewelry & Accessories Dropdown */}
              <div className="relative group">
                <span className="text-[10px] xl:text-xs uppercase tracking-widest font-medium hover:text-[#D4AF37] transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap" data-testid="nav-jewelry-accessories">
                  Jewelry & Accessories
                  <ChevronDown className="w-3 h-3 transition-transform group-hover:rotate-180" />
                </span>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-white shadow-xl border border-gray-100 min-w-[180px]">
                    <div className="py-2">
                      <Link to="/category/jewelry" className="block px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Jewelry
                      </Link>
                      <Link to="/category/accessories" className="block px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors">
                        Accessories
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Home & Living */}
              <Link to="/category/home" className="text-[10px] xl:text-xs uppercase tracking-widest font-medium hover:text-[#D4AF37] transition-colors whitespace-nowrap" data-testid="nav-home">
                Home & Living
              </Link>
              
              {/* New Arrivals */}
              <Link to="/shop?new_arrival=true" className="text-[10px] xl:text-xs uppercase tracking-widest font-medium hover:text-[#D4AF37] transition-colors whitespace-nowrap" data-testid="nav-new">
                New
              </Link>
              
              {/* Deals */}
              <Link 
                to="/shop?max_price=10" 
                className="text-[10px] xl:text-[11px] uppercase tracking-widest font-bold text-white bg-gradient-to-r from-[#D4AF37] to-[#8B1F1F] px-3 xl:px-4 py-1.5 rounded-full hover:shadow-lg hover:scale-105 transition-all flex items-center gap-1 whitespace-nowrap" 
                data-testid="nav-deals"
              >
                <Sparkles className="w-3 h-3" />
                Deals
              </Link>
              </div>
            </nav>

            {/* Actions */}
            <div className="flex items-center space-x-2 lg:space-x-3 flex-shrink-0">
              {/* Search */}
              <form onSubmit={handleSearch} className="hidden xl:block">
                <div className="relative" data-testid="search-bar">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-24 xl:w-32 2xl:w-40 px-2 py-1.5 border-b border-gray-300 bg-transparent text-sm focus:outline-none focus:border-[#D4AF37] transition-colors placeholder:text-gray-400"
                    data-testid="search-input"
                  />
                  <button type="submit" className="absolute right-0 top-1/2 transform -translate-y-1/2 p-1" data-testid="search-button">
                    <Search className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </form>

              {/* Mobile/Tablet Search Icon */}
              <button 
                className="xl:hidden p-2 hover:text-[#D4AF37] transition-colors" 
                onClick={() => navigate('/shop')}
                data-testid="search-icon-mobile"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* User Menu */}
              {user ? (
                <div className="relative group">
                  <button className="p-2 hover:text-[#D4AF37] transition-colors" data-testid="user-menu-button">
                    <User className="w-5 h-5" />
                  </button>
                  <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="bg-white shadow-xl border border-gray-100 min-w-[180px]" data-testid="user-dropdown">
                      <div className="py-2">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-xs text-gray-500">Welcome back,</p>
                          <p className="font-medium text-sm truncate">{user.email}</p>
                        </div>
                        <Link to="/account" className="block px-4 py-2.5 text-sm hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors" data-testid="account-link">
                          My Account
                        </Link>
                        <Link to="/orders" className="block px-4 py-2.5 text-sm hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors" data-testid="orders-link">
                          My Orders
                        </Link>
                        {user.is_admin && (
                          <Link to="/admin" className="block px-4 py-2.5 text-sm hover:bg-[#FAFAFA] hover:text-[#D4AF37] transition-colors" data-testid="admin-link">
                            Admin Dashboard
                          </Link>
                        )}
                        <div className="border-t border-gray-100 my-1"></div>
                        <button
                          onClick={logout}
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAFAFA] hover:text-[#8B1F1F] transition-colors flex items-center gap-2"
                          data-testid="logout-button"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Link to="/auth" className="p-2 hover:text-[#D4AF37] transition-colors" data-testid="login-link">
                  <User className="w-5 h-5" />
                </Link>
              )}

              {/* Wishlist */}
              <Link 
                to="/wishlist" 
                className="relative p-2 hover:text-[#D4AF37] transition-colors" 
                data-testid="wishlist-header-button"
              >
                <Heart className="w-5 h-5" />
                {wishlistCount > 0 && (
                  <span 
                    className="absolute -top-0.5 -right-0.5 bg-[#D4AF37] text-white text-[10px] w-5 h-5 flex items-center justify-center font-bold"
                    data-testid="wishlist-count"
                  >
                    {wishlistCount}
                  </span>
                )}
              </Link>

              {/* Cart */}
              <Link 
                to="/cart" 
                className="relative p-2 hover:text-[#D4AF37] transition-colors" 
                data-testid="cart-button"
              >
                <ShoppingBag className="w-5 h-5" />
                {cartItemsCount > 0 && (
                  <span 
                    className="absolute -top-0.5 -right-0.5 bg-[#8B1F1F] text-white text-[10px] w-5 h-5 flex items-center justify-center font-bold"
                    data-testid="cart-count"
                  >
                    {cartItemsCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white z-50 overflow-y-auto"
              data-testid="mobile-menu"
            >
              {/* Close Button */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <span 
                  className="text-2xl font-bold tracking-wider text-[#1A1A1A]"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  NEPORA
                </span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 hover:bg-gray-100 transition-colors"
                  data-testid="close-mobile-menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Search on Mobile */}
              <form onSubmit={handleSearch} className="p-4 border-b border-gray-100">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="w-full px-4 py-3 border border-gray-200 text-sm focus:outline-none focus:border-[#D4AF37]"
                  />
                  <button type="submit" className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Search className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </form>

              {/* Navigation Links */}
              <nav className="p-4">
                <Link
                  to="/category/clothing"
                  className="block py-3 text-sm uppercase tracking-widest font-medium border-b border-gray-100 hover:text-[#D4AF37] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Clothing
                </Link>
                <Link
                  to="/category/traditional-wear"
                  className="block py-3 text-sm uppercase tracking-widest font-medium border-b border-gray-100 hover:text-[#D4AF37] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Traditional
                </Link>
                <Link
                  to="/category/beauty"
                  className="block py-3 text-sm uppercase tracking-widest font-medium border-b border-gray-100 hover:text-[#D4AF37] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Beauty
                </Link>
                <Link
                  to="/category/jewelry"
                  className="block py-3 text-sm uppercase tracking-widest font-medium border-b border-gray-100 hover:text-[#D4AF37] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Jewelry & Accessories
                </Link>
                <div className="pl-4 border-b border-gray-100">
                  <Link
                    to="/category/jewelry"
                    className="block py-2 text-xs uppercase tracking-widest text-gray-600 hover:text-[#D4AF37] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Jewelry
                  </Link>
                  <Link
                    to="/category/accessories"
                    className="block py-2 text-xs uppercase tracking-widest text-gray-600 hover:text-[#D4AF37] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Accessories
                  </Link>
                </div>
                <Link
                  to="/category/home"
                  className="block py-3 text-sm uppercase tracking-widest font-medium border-b border-gray-100 hover:text-[#D4AF37] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/shop?new_arrival=true"
                  className="block py-3 text-sm uppercase tracking-widest font-medium border-b border-gray-100 hover:text-[#D4AF37] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  New Arrivals
                </Link>
                <Link
                  to="/shop?max_price=10"
                  className="flex items-center gap-2 py-3 text-sm uppercase tracking-widest font-bold border-b border-gray-100 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="bg-gradient-to-r from-[#D4AF37] to-[#8B1F1F] text-white px-3 py-1 rounded-full text-xs flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" />
                    Deals
                  </span>
                </Link>
                <div className="border-t border-gray-200 mt-4 pt-4">
                  <Link
                    to="/about"
                    className="block py-2 text-xs uppercase tracking-widest text-gray-500 hover:text-[#D4AF37] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    About Us
                  </Link>
                  <Link
                    to="/contact"
                    className="block py-2 text-xs uppercase tracking-widest text-gray-500 hover:text-[#D4AF37] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Contact
                  </Link>
                  <Link
                    to="/policies"
                    className="block py-2 text-xs uppercase tracking-widest text-gray-500 hover:text-[#D4AF37] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Policies
                  </Link>
                </div>
              </nav>

              {/* User Actions */}
              <div className="p-4 border-t border-gray-100">
                {user ? (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">Signed in as {user.email}</p>
                    <Link
                      to="/orders"
                      className="block py-2 text-sm hover:text-[#D4AF37] transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Orders
                    </Link>
                    {user.is_admin && (
                      <Link
                        to="/admin"
                        className="block py-2 text-sm hover:text-[#D4AF37] transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <button
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="flex items-center gap-2 py-2 text-sm text-[#8B1F1F]"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/auth"
                    className="block w-full py-3 text-center bg-[#1A1A1A] text-white text-sm uppercase tracking-widest font-medium hover:bg-[#D4AF37] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
